import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { HubConnection } from "@microsoft/signalr";
import { ICall } from "../../types/types";
import { MutableRefObject } from "react";
import { IAppointment, IChatMessage, IConversation, IUserChatData } from "../../types/types";
import { userApi } from "../api/user.api";
import { noDataLabel } from "../../utils/constants";

interface ChatState {
  isConnected: boolean;
  connection?: HubConnection;
  connectionError?: string;
  chatUsers: IConversation[];
  currentUserId?: number;
  showAppointmentEndPopup: string | undefined;
  call: ICall;
  callAccepted: boolean;
  callEnded: undefined | string;
  openModalCallEnded: boolean;
  startCall: boolean;
  myStream: MediaStream | undefined;
  userStream: MediaStream | undefined;
  offer: string | undefined;
  myVideo: MutableRefObject<HTMLVideoElement> | null;
  userVideo: MutableRefObject<HTMLVideoElement> | null;
  isMicMuted: boolean;
}
export const videoCallInitialState: ICall = {
  isReceivingCall: false,
  from: {
    name: "",
    avatar: "",
  },
  signal: { type: "offer" },
};
const initialState: ChatState = {
  isConnected: false,
  chatUsers: [],
  currentUserId: undefined,
  connection: undefined,
  connectionError: undefined,
  showAppointmentEndPopup: undefined,
  call: videoCallInitialState,
  offer: undefined,
  callAccepted: false,
  callEnded: undefined,
  openModalCallEnded: false,
  startCall: false,
  myStream: undefined,
  userStream: undefined,
  myVideo: null,
  userVideo: null,
  isMicMuted: false,
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    setWsConnection(state, action: PayloadAction<HubConnection>) {
      state.connection = action.payload;
    },
    clearWsConnection(state) {
      state.connection = undefined;
      state.isConnected = false;
    },
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload;
    },
    addMessage(state, { payload }: { payload: IChatMessage }) {
      state.chatUsers = state.chatUsers.map((u) =>
        u.userId === payload.senderId || u.userId === payload.receiverId
          ? { ...u, messages: [...u.messages, payload] }
          : u
      );
    },
    endCall(state, { payload }: { payload: IChatMessage | undefined }) {
      return {
        ...state,
        // call: videoCallInitialState,
        callEnded: payload?.content.split("by:")[1],
      };
    },
    setUserConnected(state, { payload }: { payload: number }) {
      return {
        ...state,
        chatUsers: state.chatUsers.map((usr) =>
          usr.userId === payload ? { ...usr, online: true } : usr
        ),
      };
    },
    setUserTyping(state, { payload }: { payload: IChatMessage }) {
      return {
        ...state,
        chatUsers: state.chatUsers.map((usr) =>
          usr.userId === payload.senderId
            ? {
                ...usr,
                isTyping: payload.content.split(":")[1] === "1",
              }
            : usr
        ),
      };
    },
    setUserDisconnected(state, { payload }: { payload: number }) {
      return {
        ...state,
        chatUsers: state.chatUsers.map((usr) =>
          usr.userId === payload ? { ...usr, online: false } : usr
        ),
      };
    },
    setCall(state, { payload }: { payload: Partial<ICall> }) {
      return {
        ...state,
        call: { ...state.call, ...payload },
      };
    },
    setCallAccepted(state, { payload }: { payload: boolean }) {
      return {
        ...state,
        callAccepted: payload,
      };
    },
    setCallEnded(state, { payload }: { payload: string | undefined }) {
      return {
        ...state,
        callEnded: payload,
      };
    },
    setOpenModalCallEnded(state, { payload }: { payload: boolean }) {
      return {
        ...state,
        openModalCallEnded: payload,
      };
    },
    setStartCall(state, { payload }: { payload: boolean }) {
      return {
        ...state,
        startCall: payload,
      };
    },
    setChatData(state, { payload }: { payload: IUserChatData }) {
      state.currentUserId = payload.currentUserId;

      const merged = payload.conversations.map((incoming) => {
        const existing = state.chatUsers.find((u) => u.userId === incoming.userId);
        return {
          ...incoming,
          isTyping: existing?.isTyping ?? false,
          appointment: existing?.appointment,
        };
      });

      const serverUserIds = new Set(payload.conversations.map((c) => c.userId));
      const appointmentOnlyUsers = state.chatUsers.filter(
        (u) => !serverUserIds.has(u.userId)
      );

      state.chatUsers = [...merged, ...appointmentOnlyUsers];
    },
    // Online-status update from ReceiveChatDataUsers
    updateChatData(state, { payload }: { payload: { userId: number; online: boolean }[] }) {
      state.chatUsers = state.chatUsers.map((u) => {
        const update = payload.find((p) => p.userId === u.userId);
        return update ? { ...u, online: update.online } : u;
      });
    },

    setMyStream(state, { payload }: { payload: MediaStream | undefined }) {
      return {
        ...state,
        myStream: payload,
      };
    },
    setUserStream(state, { payload }: { payload: MediaStream | undefined }) {
      return {
        ...state,
        userStream: payload,
      };
    },
    setOffer(state, { payload }: { payload: string }) {
      return {
        ...state,
        offer: payload,
      };
    },
    setShowAppointmentEndPopup(
      state,
      action: PayloadAction<string | undefined>
    ) {
      state.showAppointmentEndPopup = action.payload;
    },
    setConnectionError(state, action: PayloadAction<string | undefined>) {
      state.connectionError = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      userApi.endpoints.getAppointmentsByUserId.matchFulfilled,
      (state, { payload }: { payload: IAppointment[] }) => {
        const allConnectedUsers = [
          ...new Set(
            payload
              .map((appointment) => appointment.appointments.therapistsId)
              .filter((id) => typeof id === "number" && !isNaN(id))
          ),
        ];
        allConnectedUsers.forEach((userId) => {
          const appointmentDetails = payload.find(
            (appointment) => appointment.appointments.therapistsId === userId
          );

          const activeAppointment = payload.find(
            (appointment) =>
              appointment.appointments.active &&
              appointment.appointments.therapistsId === userId
            // && isToday(appointment.AppointmentDate)
          );
          const existingUserIndex = state.chatUsers.findIndex(
            (user) => user.userId === userId
          );

          if (existingUserIndex !== -1) {
            // Update existing user
            // state.chatUsers[existingUserIndex].active = !!activeAppointment;
            state.chatUsers[existingUserIndex].appointment = activeAppointment;
          } else {
            state.chatUsers.push({
              // active: !!activeAppointment,
              userId: userId,
              fullName: appointmentDetails?.therapistName || noDataLabel,
              avatar: appointmentDetails?.avatar || undefined,
              online: false,
              isTyping: false,
              messages: [],
              appointment: activeAppointment,
            });
          }
          // return {
          //   userId: a,
          //   fullName: appointmentDetails[0]?.therapistName || noDataLabel,
          //   online: false,
          //   isTyping: false,
          //   messages: [],
          //   appointment: activeAppointment,
          // };
        });
      }
    );
  },
});

export const { actions } = chatSlice;
export const {
  setWsConnection,
  clearWsConnection,
  setConnected,
  setUserDisconnected,
  setUserConnected,
  addMessage,
  setUserTyping,
  setChatData,
  updateChatData,
  endCall,
  setCall,
  setCallAccepted,
  setCallEnded,
  setOpenModalCallEnded,
  setStartCall,
  setMyStream,
  setUserStream,
  setOffer,
  setShowAppointmentEndPopup,
  setConnectionError,
} = chatSlice.actions;
export default chatSlice.reducer;
